import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {Box, IconButton, Link, Menu, MenuItem, Stack} from "@mui/material";
import {alpha, Theme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {SystemStyleObject} from "@mui/system";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import type {TFunction} from "i18next";
import React, {createContext, useContext, useState} from "react";
import {SnapshotHotel} from "./digitalSigning.types";

const FooterInkContext = createContext<string | undefined>(undefined);

/** The footer stands on the design's veil (Background) instead of the paper: the veil's ink for its texts. */
export const FooterInkProvider = FooterInkContext.Provider;

interface Props {
    hotel?: SnapshotHotel;
    t: TFunction;
    language: string;
    languages: string[];
    onLanguageChange: (language: string) => void;
    disabled?: boolean;
}

const FLAG_WIDTH = 21;
const FLAG_HEIGHT = 14;
const TOUCH_TARGET = 44;

const httpsUrl = (url?: string) => {
    const value = url?.trim();
    return value && /^https:\/\//i.test(value) ? value : undefined;
};

function Flag({language}: {language: string}) {
    const src = getIconForLanguage(language);

    if (!src) {
        return (
            <Typography component={'span'} sx={{fontSize: 14, fontWeight: 600, color: 'inherit'}}>
                {language.toUpperCase()}
            </Typography>
        );
    }

    return (
        <Box
            component={'img'}
            src={src}
            alt={''}
            sx={theme => ({
                display: 'block',
                flexShrink: 0,
                boxSizing: 'border-box',
                width: FLAG_WIDTH,
                height: FLAG_HEIGHT,
                objectFit: 'cover',
                borderRadius: '2px',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
            })}
        />
    );
}

function SigningFooter({hotel, t, language, languages, onLanguageChange, disabled}: Props) {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const veilInk = useContext(FooterInkContext);
    // On screen only: printed, the page has no veil and the paper's colors return
    const onVeil = (styles: SystemStyleObject<Theme>): SystemStyleObject<Theme> => (veilInk ? {'@media screen': styles} : {});

    const name = hotel?.name?.trim();
    const links = [
        {label: t('Privacy'), url: httpsUrl(hotel?.privacyPolicyUrl)},
        {label: t('Imprint'), url: httpsUrl(hotel?.imprintUrl)},
    ].filter(link => !!link.url);
    const showLanguageMenu = languages.length > 1;

    if (!name && !links.length && !showLanguageMenu) return null;

    return (
        <Stack
            component={'footer'}
            direction={'row'}
            flexWrap={'wrap'}
            justifyContent={'center'}
            alignItems={'center'}
            columnGap={2.5}
            sx={{minHeight: TOUCH_TARGET}}
        >
            {!!name && (
                <Typography variant={'body2'} color={'text.secondary'} sx={{fontWeight: 600, ...onVeil({color: veilInk})}}>
                    {name}
                </Typography>
            )}

            {links.map(link => (
                <Link
                    key={link.label}
                    href={link.url}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    variant={'body2'}
                    aria-label={t('{{label}} (opens in a new tab)', {label: link.label})}
                    // Doubled: over the page's link color
                    sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: TOUCH_TARGET, ...onVeil({'&&': {color: veilInk}})}}
                >
                    {link.label}
                    <OpenInNewRoundedIcon aria-hidden sx={{fontSize: 14}}/>
                </Link>
            ))}

            {showLanguageMenu && (
                <>
                    <IconButton
                        aria-label={t('Change language')}
                        aria-haspopup={'menu'}
                        aria-expanded={!!menuAnchor}
                        disabled={disabled}
                        onClick={event => setMenuAnchor(event.currentTarget)}
                        sx={{width: TOUCH_TARGET, height: TOUCH_TARGET, color: 'text.secondary', ...onVeil({color: veilInk})}}
                    >
                        <Flag language={language}/>
                    </IconButton>

                    <Menu
                        anchorEl={menuAnchor}
                        open={!!menuAnchor && !disabled}
                        onClose={() => setMenuAnchor(null)}
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    >
                        {languages.map(code => (
                            <MenuItem
                                key={code}
                                lang={code}
                                disabled={disabled}
                                selected={code === language}
                                onClick={() => {
                                    setMenuAnchor(null);
                                    if (code !== language) onLanguageChange(code);
                                }}
                                sx={{minHeight: TOUCH_TARGET, gap: 1.5}}
                            >
                                <Flag language={code}/>
                                {getNameForLanguage(code, true, true) || code.toUpperCase()}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Stack>
    );
}

export default SigningFooter;
